import DialogData from '../airtable/Dialog';
import ScriptData from '../airtable/Script';
import type { PetSpecies, PetState } from '../buruburu/pet/pet.ruleset';
import { assertNever } from '../utils';

export type DialogID = `${PetSpecies}_${number}`;
export type ScriptID = `${PetSpecies}_${number}_${number}`;

export type DialogScenes =
  | 'normal_chat'
  | 'friend_chat'
  | 'cured'
  | 'not_cured'
  | 'care_action_blocker'
  | 'initial_greetings'
  | 'friend_greetings'
  | 'friend_welcome'
  | 'friend_appreciation'
  | 'friend_goodby'
  | 'friend_farewell'
  | 'friend_kickedout'
  | 'had_visitor'
  | 'duo_inviter_invite'
  | 'duo_invitee_invited'
  | 'duo_inviter_home'
  | 'duo_invitee_home'
  | 'duo_inviter_notified'
  | 'duo_invitee_answer'
  | 'duo_invitee_accept'
  | 'duo_invitee_refuse'
  | 'duo_invitee_message_intro'
  | 'duo_invitee_message'
  | 'duo_inviter_refused';

// Pet dialog type
export type Dialog = {
  id: DialogID;
  pet_id: PetSpecies;
  scenes: DialogScenes[];
  pet_states?: PetState[];
  script_id: ScriptID;
  req_answers?: string[];
};

// Dialog data parser
export const dialogs = DialogData.reduce((dialogs, table) => {
  const data = table as typeof table & {
    pet_states: PetState[];
    req_answers: string;
  };

  dialogs[data.id] = {
    id: data.id,
    pet_id: data.pet_id,
    scenes: data.scenes.map((scene: DialogScenes) => scene), // Have to re-map TS have mapping issues for data.scenes
    pet_states: data.pet_states,
    script_id: data.script_id,
    req_answers: data.req_answers ? [data.req_answers] : undefined,
  };

  return dialogs;
}, {} as Record<DialogID, Dialog>);

// ----------------------------------
// Scripts
// ----------------------------------
export type Location =
  | 'tl'
  | 'tc'
  | 'tr'
  | 'lm'
  | 'c'
  | 'rm'
  | 'bl'
  | 'bc'
  | 'br'
  | 'aboveLeft'
  | 'floorFarRight'
  | 'floorLeft'
  | 'frontLeft';

export type Animation =
  | 'hang'
  | 'sleep'
  | 'eat'
  | 'toilet'
  | 'bath'
  | 'play'
  | 'angry'
  | 'joy'
  | 'surprised'
  | 'cry'
  | 'waive'
  | 'anxious'
  | 'evolve'
  | 'idle';

// One phrase of dialog
export type ScriptCommon = {
  id: ScriptID;
  text_jp: string;
  text_en: string;
  animation?: Animation;
  move_to?: Location;
  expression?: PetState;
  next_script_id?: ScriptID;
  case?: string;
  next_script_id_1?: ScriptID;
  next_script_id_2?: ScriptID;
  next_script_id_3?: ScriptID;
};

export type ConversationalScript = {
  type: 'conversational';
} & ScriptCommon;

// Ask player to choose options
export type SelectionalScript = {
  type: 'selectional';
  answer_var?: string;
  option_1_jp: string | number;
  option_1_en: string | number;
  option_2_jp: string | number;
  option_2_en: string | number;
  option_3_jp?: string | number;
  option_3_en?: string | number;
} & ScriptCommon;

// Ask player to enter answer
export type DescriptiveScript = {
  type: 'descriptive';
  case?: string;
  text_jp?: string;
  text_en: string;
  answer_var: string;
  question_jp: string;
  question_en: string;
  max_len: number;
  next_script_id_1?: ScriptID;
  next_script_id_2?: ScriptID;
  next_script_id_3?: ScriptID;
} & ScriptCommon;

export type Script =
  | ConversationalScript
  | SelectionalScript
  | DescriptiveScript;

// Parse scripts
export const scripts = ScriptData.reduce((scripts, table) => {
  const data = table as typeof table;

  switch (data.type) {
    case 'descriptive':
      {
        const descriptive: DescriptiveScript = data;

        scripts[descriptive.id] = {
          id: descriptive.id,
          type: data.type,
          animation: descriptive.animation,
          expression: descriptive.expression,
          case: descriptive.case,
          move_to: descriptive.move_to,
          text_jp: descriptive.text_jp,
          text_en: descriptive.text_en,
          answer_var: descriptive.answer_var,
          question_jp: descriptive.question_jp,
          question_en: descriptive.question_en,
          max_len: descriptive.max_len,
          next_script_id: descriptive.next_script_id,
          next_script_id_1: descriptive.next_script_id_1,
          next_script_id_2: descriptive.next_script_id_2,
          next_script_id_3: descriptive.next_script_id_3,
        };
      }

      break;
    case 'selectional':
      {
        const selectional: SelectionalScript = data;

        scripts[selectional.id] = {
          id: selectional.id,
          type: data.type,
          case: selectional.case,
          animation: selectional.animation,
          expression: selectional.expression,
          move_to: selectional.move_to,
          answer_var: selectional.answer_var,
          text_jp: selectional.text_jp,
          text_en: selectional.text_en,
          option_1_jp: selectional.option_1_jp,
          option_1_en: selectional.option_1_en,
          option_2_jp: selectional.option_2_jp,
          option_2_en: selectional.option_2_en,
          option_3_jp: selectional.option_3_jp,
          option_3_en: selectional.option_3_en,
          next_script_id: selectional.next_script_id,
          next_script_id_1: selectional.next_script_id_1,
          next_script_id_2: selectional.next_script_id_2,
          next_script_id_3: selectional.next_script_id_3,
        };
      }
      break;
    case 'conversational':
      {
        const conversation: ConversationalScript = data;

        scripts[conversation.id] = {
          id: conversation.id,
          type: data.type,
          text_jp: conversation.text_jp,
          text_en: conversation.text_en,
          animation: conversation.animation,
          move_to: conversation.move_to,
          expression: conversation.expression,
          case: conversation.case,
          next_script_id: conversation.next_script_id,
          next_script_id_1: conversation.next_script_id_1,
          next_script_id_2: conversation.next_script_id_2,
          next_script_id_3: conversation.next_script_id_3,
        };
      }
      break;
    default:
      assertNever('type' as never);
  }

  return scripts;
}, {} as Record<ScriptID, Script>);

export const scriptIsConversational = (
  script: Script,
): script is ConversationalScript => script.type === 'conversational';
export const scriptIsSelectional = (
  script: Script,
): script is SelectionalScript => script.type === 'selectional';
export const scriptIsDescriptive = (
  script: Script,
): script is DescriptiveScript => script.type === 'descriptive';

// Utility function to lookup a script and cast to a merged union type
export const getScript = (scriptId: ScriptID) => scripts[scriptId] as Script;
